import React, { useRef, useState, useCallback } from 'react';
import { Upload as UploadIcon, FileText, X } from 'lucide-react';

/**
 * Reusable drag-and-drop file picker.
 *
 * Props:
 *  - onFile (file: File) => void            — called for single-file mode (default)
 *  - onFiles (files: File[]) => void        — called when multiple is true
 *  - file (File | null)                     — currently selected file (controlled)
 *  - accept (string)                        — input accept attribute, e.g. "application/pdf,.pdf,.doc,.docx"
 *  - acceptExtensions (string[])            — optional client-side validation list (e.g. ['pdf','doc','docx'])
 *  - multiple (boolean)                     — allow multi-select (default false)
 *  - disabled (boolean)
 *  - hint (string)                          — small helper text shown under the prompt
 *  - label (string)                         — main prompt text (default: "Drag and drop a file here")
 *  - icon (LucideIcon)                      — icon shown when no file (defaults to Upload)
 *  - onClear ()                             — optional clear handler; shows an X chip
 *  - testId (string)
 *  - className (string)                     — extra classes for the dropzone
 *  - compact (boolean)                      — smaller paddings/icon for inline use
 */
export default function DropZone({
  onFile,
  onFiles,
  file = null,
  accept,
  acceptExtensions,
  multiple = false,
  disabled = false,
  hint,
  label,
  icon: Icon = UploadIcon,
  onClear,
  testId = 'dropzone',
  className = '',
  compact = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const validate = useCallback(
    (f) => {
      if (!acceptExtensions || acceptExtensions.length === 0) return true;
      const lower = (f.name || '').toLowerCase();
      return acceptExtensions.some((ext) => lower.endsWith(`.${ext.replace(/^\./, '').toLowerCase()}`));
    },
    [acceptExtensions]
  );

  const handleFiles = useCallback(
    (list) => {
      if (!list || list.length === 0) return;
      const arr = Array.from(list).filter(validate);
      if (arr.length === 0) return;
      if (multiple && onFiles) {
        onFiles(arr);
      } else if (onFile) {
        onFile(arr[0]);
      }
    },
    [multiple, onFile, onFiles, validate]
  );

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer?.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const onChange = (e) => {
    handleFiles(e.target.files);
    // allow re-selecting the same file
    if (inputRef.current) inputRef.current.value = '';
  };

  const open = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const promptLabel =
    label || (multiple ? 'Drag and drop files here' : 'Drag and drop a file here');

  const padding = compact ? 'p-4' : 'p-8';
  const iconSize = compact ? 28 : 40;

  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={open}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && open()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        className={`drag-drop-zone rounded-xl ${padding} text-center cursor-pointer select-none transition outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
          dragOver ? 'drag-over' : ''
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        data-testid={testId}
        aria-disabled={disabled}
      >
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <FileText className="text-primary" size={compact ? 20 : 28} />
            <div className="text-left">
              <p className="text-sm font-medium break-all">{file.name}</p>
              {typeof file.size === 'number' && (
                <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
              )}
            </div>
            {onClear && !disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                className="ml-2 p-1 rounded hover:bg-muted text-muted-foreground"
                aria-label="Remove file"
                data-testid={`${testId}-clear`}
              >
                <X size={16} />
              </button>
            )}
          </div>
        ) : (
          <>
            <Icon
              className="mx-auto mb-2 text-muted-foreground"
              size={iconSize}
              strokeWidth={1.5}
            />
            <p className={`text-foreground font-medium ${compact ? 'text-sm' : 'text-base'}`}>
              {promptLabel}
            </p>
            <p className="text-xs text-muted-foreground mt-1">or click to browse</p>
            {hint && <p className="text-xs text-muted-foreground mt-2">{hint}</p>}
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={onChange}
        className="hidden"
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
